import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { UserIcon, MailIcon, SaveIcon, LogOutIcon } from 'lucide-react';

interface Profile {
  id: string;
  full_name: string | null;
  avatar_url: string | null;
  updated_at: string;
}

interface DatabaseError extends Error {
  code?: string;
  details?: string;
}

const MyProfile = () => {
  // ...existing code from Account.tsx...
  const { user, signOut } = useAuth();
  const [loading, setLoading] = useState(false);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [message, setMessage] = useState<{
    type: 'success' | 'error' | 'info' | '';
    content: string;
  }>({ type: '', content: '' });

  useEffect(() => {
    if (user) {
      setEmail(user.email || '');
      fetchProfile();
    }
  }, [user]);

  // ...rest of the component implementation...
  const fetchProfile = async () => {
    try {
      setLoading(true);
      if (!user) return;

      // Make sure we have a valid session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) throw sessionError;
      if (!session) {
        setMessage({ type: 'error', content: 'Please sign in again.' });
        return;
      }

      // Try to fetch profile, if it doesn't exist, create it
      const { data: profile, error: fetchError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (fetchError && fetchError.code === 'PGRST116') { // Record not found
        // Create a new profile
        const defaultProfile = {
          id: user.id,
          full_name: user.email?.split('@')[0] || 'New User',
          avatar_url: null,
          updated_at: new Date().toISOString()
        };

        const { error: createError } = await supabase
          .from('profiles')
          .insert([defaultProfile]);

        if (createError) {
          console.error('Error creating profile:', createError);
          throw createError;
        }

        setFullName(defaultProfile.full_name);
        setAvatarUrl('');
        setMessage({
          type: 'success',
          content: 'Profile created successfully. Please update your information.'
        });
        return;
      } else if (fetchError) {
        throw fetchError;
      }

      if (profile) {
        setFullName(profile.full_name || '');
        setAvatarUrl(profile.avatar_url || '');
      }
    } catch (err) {
      console.error('Profile error:', err);
      const error = err as DatabaseError;
      setMessage({
        type: 'error',
        content: `Error: ${error.message || 'Failed to fetch profile'}`
      });
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      if (!user) {
        setMessage({ 
          type: 'error', 
          content: 'You must be logged in to update your profile.' 
        });
        return;
      }

      if (!fullName || fullName.trim().length < 3) {
        setMessage({ 
          type: 'error', 
          content: 'Full name must be at least 3 characters long.' 
        });
        return;
      }

      const updates = {
        id: user.id,
        full_name: fullName.trim(),
        updated_at: new Date().toISOString()
      };

      const { error: updateError } = await supabase
        .from('profiles')
        .upsert([updates], {
          onConflict: 'id'
        });

      if (updateError) throw updateError;

      setMessage({ 
        type: 'success', 
        content: 'Profile updated successfully!' 
      });
    } catch (error: any) {
      console.error('Error updating profile:', error);
      setMessage({ 
        type: 'error', 
        content: `Error updating profile: ${error.message}` 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    await signOut();
  };

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-2xl font-semibold text-gray-800 mb-8">My Profile</h1>

      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <h2 className="text-xl font-medium text-gray-800 mb-4">Profile Information</h2>
        
        {loading && (
          <div className="flex justify-center items-center py-4">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        )}
        
        {message.content && (
          <div className={`mb-4 p-3 rounded-md ${
            message.type === 'error' ? 'bg-red-50 text-red-700' : 
            message.type === 'info' ? 'bg-blue-50 text-blue-700' : 
            'bg-green-50 text-green-700'
          }`}>
            {message.content}
          </div>
        )}

        <form onSubmit={updateProfile} className="space-y-6">
          <div className="flex items-center space-x-4">
            <div className="h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
              {avatarUrl ? (
                <img 
                  src={avatarUrl} 
                  alt="Profile" 
                  className="h-16 w-16 rounded-full object-cover" 
                />
              ) : (
                <UserIcon size={32} />
              )}
            </div>
            <div>
              <h3 className="font-medium">{fullName || 'User'}</h3>
              <p className="text-sm text-gray-500">{email}</p>
            </div>
          </div>
          
          <div>
            <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-1">
              Full Name
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <UserIcon size={18} className="text-gray-400" />
              </div>
              <input
                id="fullName"
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="pl-10 w-full border border-gray-300 rounded-md py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Your full name"
              />
            </div>
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MailIcon size={18} className="text-gray-400" />
              </div>
              <input
                id="email"
                type="email"
                value={email}
                disabled
                className="pl-10 w-full border border-gray-200 bg-gray-50 rounded-md py-2 text-gray-500"
                placeholder="Your email"
              />
            </div>
            <p className="mt-1 text-xs text-gray-500">Email cannot be changed</p>
          </div>

          <div className="flex justify-end gap-4">
            <button
              type="submit"
              disabled={loading}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
            >
              {loading ? 'Saving...' : (
                <>
                  <SaveIcon size={18} className="mr-2" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <h2 className="text-xl font-medium text-gray-800 mb-4">Security</h2>
        
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            For security reasons, you'll need to contact support if you need to change your email address or reset your password.
          </p>

          <div className="pt-4">
            <button 
              onClick={handleSignOut}
              className="flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-400"
            >
              <LogOutIcon size={18} className="mr-2" />
              Sign Out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyProfile;
